import { createBrowserClient } from '@supabase/ssr';
import { SupabaseClient } from '@supabase/supabase-js';

// Validate environment variables
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[Supabase] Missing required environment variables');
  console.error('[Supabase] NEXT_PUBLIC_SUPABASE_URL:', supabaseUrl ? '✓ Set' : '✗ Missing');
  console.error('[Supabase] NEXT_PUBLIC_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✓ Set' : '✗ Missing');
}

// Create Supabase client with enhanced configuration and error handling
export const supabase: SupabaseClient = createBrowserClient(
  supabaseUrl || '',
  supabaseAnonKey || '',
  {
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
    global: {
      headers: {
        'X-Client-Info': 'signaly-web-app',
      },
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

// WebSocket connection monitor
let wsConnectionState: 'connected' | 'disconnected' | 'error' = 'disconnected';
let wsErrorCount = 0;
const MAX_WS_ERRORS = 3;
let realtimeChannel: any = null;

// Monitor realtime connection status
const monitorRealtimeConnection = () => {
  try {
    // Create a presence channel to monitor connection status
    realtimeChannel = supabase.channel('connection-monitor', {
      config: {
        presence: {
          key: 'connection-status'
        }
      }
    });

    // Subscribe and handle connection status changes
    realtimeChannel
      .on('system', { event: '*' }, (payload: any) => {
        // Handle system events for connection state
        if (payload.status === 'SUBSCRIBED') {
          wsConnectionState = 'connected';
          wsErrorCount = 0;
          console.log('[Supabase] WebSocket connection established');
        }
      })
      .subscribe((status: string) => {
        console.log('[Supabase] Subscription status:', status);
        
        switch (status) {
          case 'SUBSCRIBED':
            wsConnectionState = 'connected';
            wsErrorCount = 0;
            break;
          case 'CLOSED':
            wsConnectionState = 'disconnected';
            console.warn('[Supabase] WebSocket connection closed');
            break;
          case 'CHANNEL_ERROR': case'TIMED_OUT':
            wsConnectionState = 'error';
            wsErrorCount++;
            
            console.error('[Supabase] WebSocket error:', {
              status,
              errorCount: wsErrorCount,
              timestamp: new Date().toISOString(),
            });

            // Provide helpful error messages
            if (wsErrorCount >= MAX_WS_ERRORS) {
              console.error('[Supabase] Multiple WebSocket connection failures detected');
              console.error('[Supabase] Possible causes:');
              console.error('  1. Invalid Supabase URL or API key');
              console.error('  2. Network connectivity issues');
              console.error('  3. CORS or firewall blocking WebSocket connections');
              console.error('  4. Supabase project is paused or unavailable');
              
              // Emit custom event for UI to handle
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('supabase-connection-error', {
                  detail: {
                    message: 'WebSocket connection failed',
                    errorCount: wsErrorCount,
                    suggestions: [
                      'Check your internet connection',
                      'Verify Supabase environment variables',
                      'Check Supabase project status',
                      'Review browser console for detailed errors',
                    ],
                  },
                }));
              }
            }
            break;
        }
      });
  } catch (error) {
    console.error('[Supabase] Error setting up WebSocket monitoring:', error);
  }
};

// Initialize WebSocket monitoring only in browser
if (typeof window !== 'undefined') {
  monitorRealtimeConnection();
  
  // Add global error handler for WebSocket errors
  window.addEventListener('error', (event) => {
    // Check if it's a WebSocket-related error
    if (event.message?.includes('WebSocket') || event.error?.name === 'WebSocketError') {
      console.error('[Supabase] Global WebSocket error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
      
      // Dispatch custom event for UI handling
      window.dispatchEvent(new CustomEvent('supabase-websocket-error', {
        detail: {
          message: event.message || 'WebSocket connection error',
          error: event.error,
        },
      }));
    }
  });
}

// Export helper functions
export const getWebSocketState = () => wsConnectionState;

export const getWebSocketErrorCount = () => wsErrorCount;

export const resetWebSocketErrorCount = () => {
  wsErrorCount = 0;
};

// Cleanup function for the realtime channel
export const cleanupRealtimeMonitor = () => {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
};

// Export diagnostic function
export const diagnoseSupabaseConnection = async () => {
  const diagnostics = {
    envVariablesSet: {
      url: !!supabaseUrl,
      key: !!supabaseAnonKey,
    },
    wsConnectionState,
    wsErrorCount,
    canReachSupabase: false,
  };

  // Test basic connection
  try {
    const { data, error } = await supabase.from('trading_signals').select('count').limit(1);
    diagnostics.canReachSupabase = !error;
  } catch (error) {
    console.error('[Supabase] Connection test failed:', error);
  }

  return diagnostics;
};

function createClient(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createClient is not implemented yet.', args);
  return null;
}

export { createClient };